import type { TaskModalTask } from "@common/types";
import type { ConsumedTaskItem } from "@common/types/Inventory";
import { useState } from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "components/core/Accordion";
import { Flex } from "components/core/Flex";
import { Text } from "components/core/Text";
import { usePersistedState } from "hooks";
import useInventoryUsageSetting from "hooks/useInventoryUsageSetting";

import DontAskAgainCheckbox from "./DontAskAgainCheckbox";
import InventoryUsage from "./InventoryUsage";
import { useCompletionFormContext } from "./useCompletionFormContext";

type Props = {
  task: Pick<TaskModalTask, "id" | "name" | "taskType">;
};

const COLLAPSE_KEY = "inventory";

const InventorySection = ({ task }: Props) => {
  const { watch, setValue } = useCompletionFormContext();
  const consumedItems = watch("consumedItems");
  const inventoryDisabled = watch("inventoryDisabled");
  const inventoryPage = watch("inventoryPage");

  const [expanded, setExpanded] = usePersistedState<boolean>({
    key: "completion_modal_inventory_expanded",
    defaultValue: false
  });
  const [showOnlyTaskGardenItems, setShowOnlyTaskGardenItems] = useState(false);
  const { updateInventoryUsageSetting, isUpdating } = useInventoryUsageSetting();

  const handleDontAskAgain = (checked: boolean) => {
    setValue("inventoryDisabled", checked);
    updateInventoryUsageSetting(checked ? "off" : "manual");
  };

  const handleChange = ({ id, consumedQuantity }: ConsumedTaskItem) => {
    const updated = consumedItems.filter((item) => item.id !== id);

    if (consumedQuantity > 0) {
      updated.push({ id, consumedQuantity });
    }

    setValue("consumedItems", updated);
  };

  const consumedCount = consumedItems.length;

  return (
    <Accordion
      type="multiple"
      value={expanded ? [COLLAPSE_KEY] : []}
      onValueChange={(keys) => setExpanded(keys.includes(COLLAPSE_KEY))}
    >
      <AccordionItem value={COLLAPSE_KEY}>
        <AccordionTrigger
          extra={
            !expanded && (
              <Text color="subtle" variant="caption">
                {consumedCount > 0
                  ? `${consumedCount} item${consumedCount !== 1 ? "s" : ""}`
                  : "Skip"}
              </Text>
            )
          }
        >
          <Text color={inventoryDisabled ? "subtle" : undefined} weight="semibold">
            Record Inventory Usage
          </Text>
        </AccordionTrigger>
        <AccordionContent forceMount>
          <Flex direction="col" gap={4}>
            <Flex
              className={inventoryDisabled ? "pointer-events-none opacity-40" : undefined}
              direction="col"
              gap={1}
            >
              <InventoryUsage
                currentPage={inventoryPage}
                setCurrentPage={(v: number) => setValue("inventoryPage", v)}
                setShowOnlyTaskGardenItems={setShowOnlyTaskGardenItems}
                showOnlyTaskGardenItems={showOnlyTaskGardenItems}
                task={task}
                onChange={handleChange}
              />
            </Flex>
            <DontAskAgainCheckbox
              checked={inventoryDisabled}
              disabled={isUpdating}
              onCheckedChange={handleDontAskAgain}
            />
          </Flex>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default InventorySection;
